import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, Check, ArrowRight, MessageSquareText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { usePresentationMode } from "./PresentationContext";
import NoteDialog from "./NoteDialog";
import { useNotes } from "./NotesContext";

const shortTitle = (item) => String(item.glance_title || item.title || item.text || "Update").trim().split(/\s+/).slice(0, 6).join(" ").replace(/[.!?;:,]+$/, "");

export const SelectedCenterSummary = ({ product, onOpenDetails, mobile = false }) => {
  const reducedMotion = useReducedMotion();
  const presenting = usePresentationMode();
  const Bullet = presenting ? "div" : Button;
  const { noteCount } = useNotes();
  const [noteTarget, setNoteTarget] = useState(null);
  const prefix = mobile ? "wheel-mobile-summary" : "wheel-center-summary";
  // Never use one_line_status, attention or blocked content in this presentation preview.
  const sections = [
    { key: "done", label: "What’s done", Icon: Check, empty: "No milestones recorded yet." },
    { key: "next", label: "What’s next", Icon: ArrowRight, empty: "No next steps recorded yet." },
  ];
  const linkedProduct = { slug: product.slug, name: product.display_name, hex: product.swatch };

  return (
    <>
      <Card className="wheel-center-summary-card" style={{ "--product-swatch": product.swatch }} data-testid={prefix}>
        <motion.div
          key={product.slug}
          initial={reducedMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.2 }}
        >
          <div className="flex items-center gap-2">
            <span className="wheel-product-dot" aria-hidden="true" />
            <p className="eyebrow" data-testid={`${prefix}-label`}>At a glance</p>
          </div>
          <h3 className="mt-1 font-heading text-2xl font-semibold leading-tight" data-testid={`${prefix}-title`}>{product.display_name}</h3>
          {product.tagline && <p className="mt-1 text-xs leading-relaxed text-muted-foreground" data-testid={`${prefix}-tagline`}>{product.tagline}</p>}
          {product.content_notice && <p className="mt-2 text-xs leading-relaxed text-muted-foreground" data-testid={`${prefix}-content-notice`}>{product.content_notice}</p>}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {sections.map(({ key, label, Icon, empty }) => {
              const items = (product[key] || []).filter(Boolean).filter((item) => !presenting || !item.excluded_from_presentation).slice(0, 2);
              return (
                <div key={key} className="min-w-0" data-testid={`${prefix}-${key}`}>
                  <h4 className="mb-1 flex items-center gap-1.5 text-xs font-semibold" data-testid={`${prefix}-${key}-heading`}><Icon size={14} aria-hidden="true" />{label}</h4>
                  {items.length ? (
                    <ul className="space-y-1">
                      {items.map((item, index) => (
                        <li key={item.key || index}>
                          <Bullet
                            {...(!presenting ? {
                              variant: "ghost",
                              onClick: () => setNoteTarget({ item, section: `prod-${key}`, product: linkedProduct }),
                              title: shortTitle(item),
                              "aria-label": `Leave a note: ${shortTitle(item)}${item.excluded_from_presentation ? "; hidden in Presentation Mode" : ""}`,
                            } : {})}
                            className={`wheel-summary-bullet ${presenting ? "wheel-summary-bullet--readonly" : ""} ${item.excluded_from_presentation ? "glance-item-excluded" : ""}`}
                            data-excluded={Boolean(item.excluded_from_presentation)}
                            data-item-key={item.key}
                            data-testid={`${prefix}-${key}-item-${index}`}
                          >
                            <span className="wheel-summary-dot" aria-hidden="true" />
                            <span className="min-w-0 flex-1 truncate" data-testid={`${prefix}-${key}-title-${index}`}>{shortTitle(item)}</span>
                            {item.is_new && <span className="new-marker shrink-0 rounded-full px-1 text-xs" data-testid={`${prefix}-${key}-new-${index}`}>New</span>}
                            {!presenting && noteCount(item.key) > 0 && <span className="inline-flex shrink-0 items-center gap-1" data-testid={`${prefix}-${key}-notes-${index}`}><MessageSquareText size={12} aria-hidden="true" />{noteCount(item.key)}</span>}
                          </Bullet>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="py-2 text-xs leading-relaxed text-muted-foreground" data-testid={`${prefix}-${key}-empty`}>{product.empty_messages?.[key] || empty}</p>}
                </div>
              );
            })}
          </div>
          <Button variant="ghost" className="wheel-summary-details mt-3" onClick={onOpenDetails} data-testid={`${prefix}-open-details`}>
            Full detail below <ArrowDown size={14} aria-hidden="true" />
          </Button>
        </motion.div>
      </Card>
      {!presenting && <NoteDialog open={Boolean(noteTarget)} onOpenChange={(open) => { if (!open) setNoteTarget(null); }} item={noteTarget?.item} product={noteTarget?.product} section={noteTarget?.section} />}
    </>
  );
};
